/**
 * AI 项目体检面板（可视化③）：事实卡片 + 规则风险清单 + 数字锁死的 AI 解读。
 * 挂在队伍页「工作台」Tab 顶部：成队前后都能看（成队前进度类事实为 0）。
 */
import { workbenchApi } from '../api/workbench.js';
import { useQuery } from '@tanstack/react-query';
import type { TeamHealthDto } from '../api/types.js';
import { ErrorState } from './ErrorState.js';
import { Loading } from './Loading.js';
import { Tag } from './Tag.js';

const RISK_TONE: Record<TeamHealthDto['risks'][number]['level'], 'danger' | 'warn' | 'ok'> = {
  danger: 'danger',
  warn: 'warn',
  info: 'ok',
};

export function TeamHealthPanel({ teamId }: { teamId: string }) {
  const healthQuery = useQuery({
    queryKey: ['team-health', teamId],
    queryFn: () => workbenchApi.health(teamId),
    staleTime: 30_000,
  });

  if (healthQuery.isPending) {
    return <Loading label="正在体检…" rows={2} />;
  }
  if (healthQuery.isError || healthQuery.data === undefined) {
    return <ErrorState error={healthQuery.error} title="读取项目体检失败" onRetry={() => void healthQuery.refetch()} />;
  }

  const health = healthQuery.data;
  const facts = health.facts;

  return (
    <div className="stack-sm" aria-label="AI 项目体检">
      <div className="health-facts">
        <div className="health-fact">
          <span className="health-fact-value">{facts.overallPct}%</span>
          <span className="health-fact-label">整体进度</span>
        </div>
        <div className={`health-fact ${facts.overdue > 0 ? 'health-fact-danger' : ''}`}>
          <span className="health-fact-value">{facts.overdue}</span>
          <span className="health-fact-label">已逾期任务</span>
        </div>
        <div className={`health-fact ${facts.dueSoon > 0 ? 'health-fact-warn' : ''}`}>
          <span className="health-fact-value">{facts.dueSoon}</span>
          <span className="health-fact-label">7 天内到期</span>
        </div>
        <div className="health-fact">
          <span className="health-fact-value">{facts.members}</span>
          <span className="health-fact-label">在队人数</span>
        </div>
        <div className="health-fact">
          <span className="health-fact-value">{facts.decisions7d}</span>
          <span className="health-fact-label">近 7 天决策</span>
        </div>
        <div className="health-fact">
          <span className="health-fact-value">{facts.feedbackCount}</span>
          <span className="health-fact-label">导师反馈</span>
        </div>
      </div>

      <div className="stack-xs" aria-label="规则风险">
        {health.risks.map((risk) => (
          <div key={risk.code} className="row-tight">
            <Tag tone={RISK_TONE[risk.level]}>{risk.level === 'danger' ? '紧急' : risk.level === 'warn' ? '关注' : '正常'}</Tag>
            <span className="small">{risk.message}</span>
          </div>
        ))}
      </div>

      <div className="feedback-suggestion">
        <div className="row-tight">
          <Tag tone={health.aiSummary.via === 'llm' ? 'primary' : 'muted'}>
            AI 体检解读 · via {health.aiSummary.via === 'llm' ? 'LLM 润色' : '规则模板'}
          </Tag>
          <span className="muted small">数字由代码算死，LLM 仅润色</span>
        </div>
        <p className="wrap-anywhere">{health.aiSummary.text}</p>
      </div>
    </div>
  );
}
